/**
 * ProductCheckoutModals — portaled modal + sheet layer for the unified
 * checkout funnel (MS61a parity across all concierge surfaces).
 *
 * Renders into document.body so nested scroll containers (floating
 * widget, dashboard drawer) never clip ProductDetailModal or
 * OrderConfirmSheet.
 */

import { createPortal } from "react-dom";
import { ProductDetailModal } from "@/components/product-detail/ProductDetailModal";
import { OrderConfirmSheet } from "@/components/order-confirm/OrderConfirmSheet";
import type { ProductCheckoutCameFrom, ProductCheckoutFlow } from "@/hooks/useProductCheckoutFlow";
import type { FulfillmentReturnContext } from "@/lib/fulfillmentReturn";

export interface ProductCheckoutModalsProps {
  flow: ProductCheckoutFlow;
  recipientName?: string;
  recipientId?: string | null;
  occasion?: string;
  defaultDate?: string;
  defaultAddress?: string;
  isAnonymous?: boolean;
  guestSessionId?: string | null;
  chatSessionId?: string | null;
  celebrationId?: string | null;
  bulkCheckout?: {
    bulkRunId: string;
    bulkPeriodKey: string;
    celebrationIds: string[];
  } | null;
  onSubmitted?: (requestId: string) => void;
  /** Fired after the checkout sheet closes (e.g. clear celebration context). */
  onCheckoutClose?: () => void;
  /** Override cameFrom on the flow when a parent passes a more specific surface. */
  cameFrom?: ProductCheckoutCameFrom;
  /** When set (e.g. Orders needs-order rail), show an explicit return affordance. */
  fulfillmentReturn?: FulfillmentReturnContext | null;
}

export function ProductCheckoutModals({
  flow,
  recipientName = "",
  recipientId = null,
  occasion = "other",
  defaultDate,
  defaultAddress,
  isAnonymous = false,
  guestSessionId = null,
  chatSessionId = null,
  celebrationId = null,
  bulkCheckout = null,
  onSubmitted,
  onCheckoutClose,
  cameFrom,
  fulfillmentReturn = null,
}: ProductCheckoutModalsProps) {
  const {
    viewingSku,
    confirmingSku,
    confirmingAddons,
    handleConfirmFromModal,
    handleCloseModal,
    handleCloseSheet,
  } = flow;

  const surface = cameFrom ?? flow.cameFrom;

  if (typeof document === "undefined") return null;
  if (!viewingSku && !confirmingSku) return null;

  return createPortal(
    <>
      {viewingSku && (
        <ProductDetailModal
          sku={viewingSku}
          recipientName={recipientName || null}
          cameFrom={surface}
          fulfillmentReturn={fulfillmentReturn}
          onClose={handleCloseModal}
          onSend={handleConfirmFromModal}
        />
      )}

      {confirmingSku && (
        <OrderConfirmSheet
          key={`${confirmingSku.sku_id}-${celebrationId ?? "none"}-${recipientId ?? "none"}`}
          sku={confirmingSku}
          selectedAddons={confirmingAddons}
          recipientName={recipientName}
          recipientId={recipientId}
          occasion={occasion}
          defaultDate={defaultDate}
          defaultAddress={defaultAddress}
          cameFrom={surface}
          fulfillmentReturn={fulfillmentReturn}
          isAnonymous={isAnonymous}
          guestSessionId={guestSessionId}
          chatSessionId={chatSessionId}
          celebrationId={celebrationId}
          bulkCheckout={bulkCheckout}
          onClose={() => {
            handleCloseSheet();
            onCheckoutClose?.();
          }}
          onSubmitted={onSubmitted}
        />
      )}
    </>,
    document.body,
  );
}
